import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { formatBucket, n0, nCompact, TIMESERIES_METRICS } from './usageFormat';
import styles from './UsageAnalyticsPanel.module.scss';

// Palette cycles for split-by series; primary brand color always leads (single-series case).
const PALETTE = ['#e36250', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#6366f1'];

function buildLinePath(points) {
  if (!points.length) return '';
  return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ');
}

/**
 * Hand-rolled SVG line chart (no chart library in this project — same technique
 * as Dashboard/ActivityTimelineChart.jsx), extended to draw one line per series
 * when the response carries `split_by`, with a client-side metric toggle since
 * the API already returns tokens/calls/cost per point in a single response.
 */
const UsageTimeseriesChart = ({ data }) => {
  const [metric, setMetric] = useState('total_tokens');
  const [hover, setHover] = useState(null);

  const grain = data?.granularity || 'day';

  const chart = useMemo(() => {
    const points = Array.isArray(data?.points) ? data.points : [];
    if (!points.length) return null;

    const seriesNames = Array.from(new Set(points.map((p) => p.series ?? '__single__')));
    const buckets = Array.from(new Set(points.map((p) => p.bucket))).sort();

    const seriesData = seriesNames.map((name) => {
      const byBucket = new Map(
        points.filter((p) => (p.series ?? '__single__') === name).map((p) => [p.bucket, p])
      );
      return {
        name: name === '__single__' ? null : name,
        values: buckets.map((b) => Number(byBucket.get(b)?.[metric] ?? 0)),
      };
    });

    const maxY = Math.max(1, ...seriesData.flatMap((s) => s.values));
    const w = 1000;
    const h = 260;
    const left = 56;
    const right = 10;
    const top = 14;
    const bottom = 40;
    const plotW = w - left - right;
    const plotH = h - top - bottom;
    const xScale = (i) => left + (buckets.length <= 1 ? 0 : (i / (buckets.length - 1)) * plotW);
    const yScale = (v) => top + ((maxY - v) / maxY) * plotH;

    const lines = seriesData.map((s, si) => ({
      name: s.name,
      color: PALETTE[si % PALETTE.length],
      pts: s.values.map((v, i) => ({ x: xScale(i), y: yScale(v), v })),
    }));

    const tickCount = 5;
    const yTicks = Array.from({ length: tickCount }, (_, i) => {
      const value = (maxY / (tickCount - 1)) * i;
      return { value, y: yScale(value), label: nCompact(value) };
    });

    const maxXTicks = 6;
    const xTickCount = Math.min(maxXTicks, buckets.length);
    const xTicks = [];
    for (let i = 0; i < xTickCount; i++) {
      const idx = buckets.length === 1 ? 0 : Math.round((i / (xTickCount - 1)) * (buckets.length - 1));
      xTicks.push({ x: xScale(idx), label: formatBucket(buckets[idx], grain) });
    }

    return { lines, buckets, yTicks, xTicks, view: { w, h, left, right, top, bottom, plotW } };
  }, [data, metric, grain]);

  const handleMove = (e) => {
    if (!chart) return;
    const rect = e.currentTarget.getBoundingClientRect();
    if (!rect.width) return;
    const x = ((e.clientX - rect.left) / rect.width) * chart.view.w;
    const { left, plotW } = chart.view;
    const t = plotW <= 0 ? 0 : (x - left) / plotW;
    const idx = Math.max(0, Math.min(chart.buckets.length - 1, Math.round(t * (chart.buckets.length - 1))));
    setHover(idx);
  };

  if (!chart) {
    return (
      <Box className={styles.chartEmpty}>
        <Typography variant="body2" color="text.secondary">
          No trend data for this range.
        </Typography>
      </Box>
    );
  }

  const metricLabel = TIMESERIES_METRICS.find((m) => m.value === metric)?.label || metric;
  const formatValue = (v) => (metric === 'estimated_cost' ? `$${Number(v || 0).toFixed(2)}` : n0(v));
  const baseline = chart.view.h - chart.view.bottom;
  const hoverX = hover != null ? chart.lines[0]?.pts[hover]?.x : null;

  return (
    <Box>
      <Box className={styles.metricToggle}>
        {TIMESERIES_METRICS.map((m) => (
          <button
            type="button"
            key={m.value}
            className={`${styles.metricBtn} ${metric === m.value ? styles.metricBtnActive : ''}`}
            onClick={() => setMetric(m.value)}
          >
            {m.label}
          </button>
        ))}
      </Box>

      <Box className={styles.chartBox}>
        <svg
          className={styles.chartSvg}
          viewBox={`0 0 ${chart.view.w} ${chart.view.h}`}
          preserveAspectRatio="xMidYMid meet"
          height={230}
          onMouseMove={handleMove}
          onMouseEnter={handleMove}
          onMouseLeave={() => setHover(null)}
          role="img"
          aria-label={`Usage ${metricLabel} over time`}
        >
          <line
            x1={chart.view.left}
            y1={baseline}
            x2={chart.view.w - chart.view.right}
            y2={baseline}
            stroke="rgba(148,163,184,0.25)"
            strokeWidth="1"
          />
          <line
            x1={chart.view.left}
            y1={chart.view.top}
            x2={chart.view.left}
            y2={baseline}
            stroke="rgba(148,163,184,0.25)"
            strokeWidth="1"
          />

          {chart.yTicks.map((tick, idx) => (
            <g key={idx}>
              <line x1={chart.view.left - 5} y1={tick.y} x2={chart.view.left} y2={tick.y} stroke="rgba(148,163,184,0.25)" strokeWidth="1" />
              <text x={chart.view.left - 8} y={tick.y + 3} fill="var(--text-secondary)" fontSize="9" fontWeight="500" textAnchor="end">
                {tick.label}
              </text>
            </g>
          ))}

          {chart.xTicks.map((tick, idx) => (
            <g key={idx}>
              <line x1={tick.x} y1={baseline} x2={tick.x} y2={baseline + 5} stroke="rgba(148,163,184,0.25)" strokeWidth="1" />
              <text x={tick.x} y={baseline + 16} fill="var(--text-secondary)" fontSize="9" fontWeight="500" textAnchor="middle">
                {tick.label}
              </text>
            </g>
          ))}

          {chart.lines.map((line) => (
            <path key={line.name || '_single'} d={buildLinePath(line.pts)} fill="none" stroke={line.color} strokeWidth="2.2" />
          ))}

          {hoverX != null && <line x1={hoverX} y1={chart.view.top} x2={hoverX} y2={baseline} stroke="rgba(148,163,184,0.25)" strokeWidth="1" />}

          {hover != null &&
            chart.lines.map((line) => {
              const p = line.pts[hover];
              if (!p) return null;
              return <circle key={line.name || '_single'} cx={p.x} cy={p.y} r={4} fill={line.color} stroke="white" strokeWidth="1.5" />;
            })}
        </svg>

        {hover != null && (
          <Box className={styles.chartTooltip} sx={{ left: `${Math.min(Math.max((hoverX / chart.view.w) * 100, 10), 90)}%` }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
              {formatBucket(chart.buckets[hover], grain)}
            </Typography>
            {chart.lines.map((line) => (
              <Typography key={line.name || '_single'} variant="caption" sx={{ display: 'block', color: line.color, fontWeight: 600 }}>
                {line.name || metricLabel}: {formatValue(line.pts[hover]?.v)}
              </Typography>
            ))}
          </Box>
        )}
      </Box>

      {chart.lines.length > 1 && (
        <Box className={styles.chartLegendRow}>
          {chart.lines.map((line) => (
            <Box key={line.name} className={styles.legendItem}>
              <span className={styles.legendDot} style={{ background: line.color }} />
              {line.name}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default UsageTimeseriesChart;
